import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import EventIcon from '@mui/icons-material/Event';
import { format } from 'date-fns';
import { supabase } from '../../../lib/supabase';
import { LeaveRequest, Worker } from '../../../types/worker';

interface LeaveManagementProps {
  farmId: string;
  workers: Worker[];
}

const LeaveManagement: React.FC<LeaveManagementProps> = ({ farmId, workers }) => {
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [newRequest, setNewRequest] = useState<Partial<LeaveRequest>>({
    leaveType: 'sick',
    status: 'pending'
  });

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('leave_requests')
        .select('*')
        .eq('farm_id', farmId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Map snake_case to camelCase
      const mappedRequests = (data || []).map((r: any) => ({
        ...r,
        workerId: r.worker_id,
        farmId: r.farm_id,
        startDate: r.start_date,
        endDate: r.end_date,
        leaveType: r.leave_type,
        reviewedBy: r.reviewed_by,
        reviewedAt: r.reviewed_at,
        createdAt: r.created_at
      }));

      setRequests(mappedRequests);
    } catch (err: any) {
      console.error('Error fetching leave requests:', err);
      setError('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (farmId) fetchRequests();
  }, [farmId]);

  const handleStatusUpdate = async (id: string, status: 'approved' | 'rejected') => {
    try {
      const { error } = await supabase
        .from('leave_requests')
        .update({ 
          status,
          reviewed_at: new Date().toISOString(),
          reviewed_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', id);

      if (error) throw error;
      await fetchRequests();
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update request status');
    }
  };

  const handleSubmitRequest = async () => {
    try {
      if (!newRequest.workerId || !newRequest.startDate || !newRequest.endDate) {
        setError('Please fill in all required fields');
        return;
      }

      const { error } = await supabase
        .from('leave_requests')
        .insert([{
          worker_id: newRequest.workerId,
          farm_id: farmId,
          start_date: newRequest.startDate,
          end_date: newRequest.endDate,
          leave_type: newRequest.leaveType,
          reason: newRequest.reason,
          status: 'pending'
        }]);

      if (error) throw error;
      
      setOpenDialog(false);
      setNewRequest({ leaveType: 'sick', status: 'pending' });
      await fetchRequests();
    } catch (err) {
      console.error('Error creating request:', err);
      setError('Failed to create leave request');
    }
  };

  const getWorkerName = (id: string) => {
    return workers.find(w => w.id === id)?.name || 'Unknown Worker';
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const historyRequests = requests.filter(r => r.status !== 'pending');

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Leave Requests</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          New Request
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Pending Requests */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon color="warning" /> Pending Approvals ({pendingRequests.length})
              </Typography>
              
              {loading ? <CircularProgress size={24} /> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Worker</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Dates</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingRequests.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">No pending requests</TableCell>
                        </TableRow>
                      ) : (
                        pendingRequests.map((request) => (
                          <TableRow key={request.id}>
                            <TableCell>{getWorkerName(request.workerId)}</TableCell>
                            <TableCell>
                              <Chip label={request.leaveType} size="small" sx={{ textTransform: 'capitalize' }} />
                            </TableCell>
                            <TableCell>
                              {format(new Date(request.startDate), 'MMM d')} - {format(new Date(request.endDate), 'MMM d, yyyy')}
                            </TableCell>
                            <TableCell>{request.reason || '-'}</TableCell>
                            <TableCell align="right">
                              <IconButton 
                                color="success" 
                                size="small"
                                onClick={() => handleStatusUpdate(request.id, 'approved')}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                              <IconButton 
                                color="error" 
                                size="small"
                                onClick={() => handleStatusUpdate(request.id, 'rejected')}
                              >
                                <CancelIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* History */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon color="action" /> Request History
              </Typography>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Worker</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Dates</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reviewed By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">No history available</TableCell>
                      </TableRow>
                    ) : (
                      historyRequests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>{getWorkerName(request.workerId)}</TableCell>
                          <TableCell>
                            <Chip label={request.leaveType} size="small" sx={{ textTransform: 'capitalize' }} />
                          </TableCell>
                          <TableCell>
                            {format(new Date(request.startDate), 'MMM d')} - {format(new Date(request.endDate), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={request.status} 
                              size="small" 
                              color={request.status === 'approved' ? 'success' : 'error'}
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {request.reviewedAt ? format(new Date(request.reviewedAt), 'MMM d, HH:mm') : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* New Request Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Leave Request</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Worker"
              fullWidth
              value={newRequest.workerId || ''}
              onChange={(e) => setNewRequest({ ...newRequest, workerId: e.target.value })}
            >
              {workers.map((worker) => (
                <MenuItem key={worker.id} value={worker.id}>
                  {worker.name}
                </MenuItem>
              ))}
            </TextField>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  type="date"
                  label="Start Date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={newRequest.startDate || ''}
                  onChange={(e) => setNewRequest({ ...newRequest, startDate: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type="date"
                  label="End Date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={newRequest.endDate || ''}
                  onChange={(e) => setNewRequest({ ...newRequest, endDate: e.target.value })}
                />
              </Grid>
            </Grid>

            <TextField
              select
              label="Leave Type"
              fullWidth
              value={newRequest.leaveType || 'sick'}
              onChange={(e) => setNewRequest({ ...newRequest, leaveType: e.target.value as any })}
            >
              <MenuItem value="sick">Sick Leave</MenuItem>
              <MenuItem value="vacation">Vacation</MenuItem>
              <MenuItem value="personal">Personal</MenuItem>
              <MenuItem value="maternity">Maternity</MenuItem>
              <MenuItem value="paternity">Paternity</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>

            <TextField
              label="Reason"
              multiline
              rows={3}
              fullWidth
              value={newRequest.reason || ''}
              onChange={(e) => setNewRequest({ ...newRequest, reason: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitRequest} variant="contained">Submit Request</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveManagement;
